import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { Book } from '../../content'

// =============================================================================
// BOOK METRICS MONTHLY - Lookup table (generated by `cron / metrics-monthly`)
// Grain: exactly one row per (bookId, month). `month` is start-of-month UTC.
// Producer RECOMPUTES the current month every run from event rows
// (LoveBook / ReadingSession filtered by createdAt >= month) and UPSERTs via
// onConflictDoUpdate(target: [bookId, month]) — idempotent, no source flags.
// Backs the all-time-independent "Populer bulan ini" ranking.
// =============================================================================

export const BookMetricsMonthly = pgTable(
  'book_metrics_monthly',
  {
    id: uuid().defaultRandom().primaryKey(),
    bookId: uuid()
      .references(() => Book.id, { onDelete: 'cascade' })
      .notNull(),
    // Start-of-month UTC (e.g. 2026-06-01T00:00:00Z). Half of the upsert key.
    month: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    likesCount: integer().notNull().default(0),
    viewsCount: integer().notNull().default(0),
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_book_metrics_monthly_book').on(t.bookId),
    index('idx_book_metrics_monthly_month').on(t.month),
    index('idx_book_metrics_monthly_month_likes').on(t.month, t.likesCount),
    index('idx_book_metrics_monthly_month_views').on(t.month, t.viewsCount),
    unique('uq_book_metrics_monthly_book_month').on(t.bookId, t.month),
  ],
)

export const bookMetricsMonthlyRelations = relations(BookMetricsMonthly, ({ one }) => ({
  book: one(Book, {
    fields: [BookMetricsMonthly.bookId],
    references: [Book.id],
  }),
}))

export const InsertBookMetricsMonthlySchema = createInsertSchema(BookMetricsMonthly).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
export const UpdateBookMetricsMonthlySchema = createUpdateSchema(BookMetricsMonthly).partial().omit({
  id: true,
  bookId: true,
  month: true,
  createdAt: true,
  updatedAt: true,
})
export const SelectBookMetricsMonthlySchema = createSelectSchema(BookMetricsMonthly)

export type InsertBookMetricsMonthly = typeof BookMetricsMonthly.$inferInsert
export type UpdateBookMetricsMonthly = z.infer<typeof UpdateBookMetricsMonthlySchema>
export type SelectBookMetricsMonthly = typeof BookMetricsMonthly.$inferSelect
